import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAuthStore } from '../stores/authStore';
import { useUIStore } from '../stores/uiStore';
import { ThemeToggleIconOnly } from '../components/common/theme-toggle';
import { parseJWT } from '../lib/utils';
import { API_BASE, OAUTH2_CLIENT_ID } from '../lib/env';
import { setRefreshTokenHandler, getProfile } from '../services/api';

interface JWTPayload {
  sub?: string;
  role?: string;
  org_id?: string;
  name?: string;
  email_verified?: boolean;
}

export function CustomLoginScreen() {
  const { clearError } = useAuth();
  const { isAuthenticated, setTokens, setUser } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLocalLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  useEffect(() => {
    const savedDarkMode = useUIStore.getState().darkMode;
    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
      return;
    }
    clearError();
  }, [isAuthenticated, navigate, clearError]);

  const handleCustomLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setLocalLoading(true);
    clearError();

    try {
      if (!email || !password) {
        setLoginError('Please fill in all fields');
        setLocalLoading(false);
        return;
      }

      const loginResponse = await fetch(`${API_BASE}/api/v1/users/login`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          client_id: OAUTH2_CLIENT_ID,
          email,
          password
        }),
      });

      if (!loginResponse.ok) {
        const errorData = await loginResponse.json().catch(() => ({ message: 'Login failed' }));
        throw new Error(errorData.message || errorData.error || `Login failed (${loginResponse.status})`);
      }

      const response = await loginResponse.json();

      let userData;
      let accessToken = '';
      let refreshToken = '';
      let expiresIn = 3600;
      let idToken = '';

      if (response.user) {
        userData = response.user;
        accessToken = response.access_token || '';
        refreshToken = response.refresh_token || '';
        expiresIn = response.expires_in || 3600;
        idToken = response.id_token || '';
      } else {
        const profileResponse = await getProfile() as { user?: Record<string, unknown> };
        userData = (profileResponse.user || profileResponse) as Record<string, unknown>;
        accessToken = response.access_token || '';
        refreshToken = response.refresh_token || '';
      }

      if (accessToken) {
        parseJWT(accessToken) as JWTPayload;
        setTokens({
          accessToken,
          refreshToken,
          expiresAt: Date.now() + (expiresIn * 1000),
          idToken,
        });

        localStorage.setItem('oauth_access_token', accessToken);
        if (refreshToken) {
          localStorage.setItem('oauth_refresh_token', refreshToken);
        }

        const refreshHandler = async () => {
          const refreshResponse = await fetch(`${API_BASE}/api/v1/users/refresh`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: refreshToken }),
          }).then(r => r.json());

          if (refreshResponse.error) {
            throw new Error('Token refresh failed');
          }

          const newAccessToken = refreshResponse.access_token || refreshResponse.accessToken;
          const newRefreshToken = refreshResponse.refresh_token || refreshResponse.refreshToken || refreshToken;
          const newExpiresIn = refreshResponse.expires_in || 3600;

          setTokens({
            accessToken: newAccessToken,
            refreshToken: newRefreshToken,
            expiresAt: Date.now() + (newExpiresIn * 1000),
          });

          localStorage.setItem('oauth_access_token', newAccessToken);
          localStorage.setItem('oauth_refresh_token', newRefreshToken);

          return newRefreshToken;
        };

        setRefreshTokenHandler(refreshHandler);
      }

      setUser({
        id: userData.id || userData.sub || email,
        email: userData.email,
        name: userData.name || email.split('@')[0],
        role: (userData.role as 'user' | 'admin' | 'super_admin') || 'admin',
        is_super_admin: userData.role === 'super_admin' || userData.is_super_admin,
        org_id: userData.org_id || userData.organization_id || '',
        locked: userData.locked || false,
        locked_until: userData.locked_until || null,
        email_verified: userData.email_verified || false,
        created_at: userData.created_at || new Date().toISOString(),
        updated_at: userData.updated_at || new Date().toISOString(),
      });

      navigate('/dashboard');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed. Please check your credentials.';
      setLoginError(errorMessage);
      setLocalLoading(false);
    }
  };

  return (
    <div className="flex h-screen items-center justify-center p-4 bg-gradient-to-br from-primary-50 via-secondary-50 to-accent-50 overflow-y-auto">
      <div className="w-full max-w-md">
        <div className="modern-card-gradient overflow-hidden">
          <div className="bg-gradient-to-r from-primary-500 via-secondary-500 to-accent-500 p-6 md:p-8 text-white">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 backdrop-blur-md rounded-xl p-2 shadow-inner flex-shrink-0">
                  <img src="/logo.png" alt="Logo" className="h-8 w-auto object-contain" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold">Custom Login</h2>
              </div>
              <ThemeToggleIconOnly />
            </div>
            <p className="text-primary-100 text-sm md:text-base">
              Sign in with your email and password
            </p>
          </div>

          <div className="p-6 md:p-8 space-y-4 md:space-y-6">
            {loginError && (
              <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <svg className="h-5 w-5 text-danger-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-danger-800">Login Failed</h3>
                    <p className="text-sm text-danger-700 mt-1 break-words">{loginError}</p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleCustomLogin} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter your email"
                  disabled={loading}
                  required
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter your password"
                  disabled={loading}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary btn-ripple group relative overflow-hidden"
              >
                <span className="relative z-10 flex items-center justify-center gap-2 text-base md:text-lg font-semibold">
                  {loading ? (
                    <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                    </svg>
                  )}
                  {loading ? 'Signing in...' : 'Sign In'}
                </span>
              </button>
            </form>

            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">Or continue with</span>
              </div>
            </div>

            <div className="text-center text-sm text-gray-600 dark:text-gray-400 space-y-2">
              <p>Use your organization credentials to sign in</p>
              <p className="text-xs text-gray-400">
                Direct API authentication
              </p>
            </div>

            <div className="pt-4 border-t border-gray-200 dark:border-gray-700 text-center space-y-2">
              <Link
                to="/login"
                className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors inline-flex items-center gap-1"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
                Login with OAuth2 instead
              </Link>
              <br />
              <Link
                to="/custom/forgot-password"
                className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors inline-flex items-center gap-1"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
                Forgot your password?
              </Link>
              <br />
              <Link
                to="/setup"
                className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors inline-flex items-center gap-1"
              >
                Need to setup super admin?
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-4 text-center text-sm text-gray-500 dark:text-gray-400">
          <p>Secure authentication via API</p>
        </div>
      </div>
    </div>
  );
}
